import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { EventsService } from "@/api/events";

// Sample event data with mapping by ID - in a real app this would come from an API
const eventsData = {
  "1": {
    id: "1",
    title: "Tech Innovation Summit 2023",
    description:
      "Join industry leaders for discussions on emerging technologies and digital transformation strategies.",
    date: "2023-12-15",
    startTime: "09:00",
    endTime: "17:00",
    location: "San Francisco Convention Center",
    address: "747 Howard St, San Francisco, CA 94103",
    image:
      "https://images.unsplash.com/photo-1540575467063-178a50c2df87?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80",
    ticketTypes: [
      {
        id: "standard",
        name: "Standard Ticket",
        price: 299,
        description: "Full event access for one attendee",
      },
      {
        id: "vip",
        name: "VIP Ticket",
        price: 499,
        description: "Premium access with VIP seating and exclusive networking",
      },
      {
        id: "early",
        name: "Early Bird",
        price: 199,
        description: "Limited availability, first come first served",
      },
    ],
    customFields: [
      { id: "company", label: "Company Name", type: "text", required: true },
      { id: "jobTitle", label: "Job Title", type: "text", required: true },
      {
        id: "dietary",
        label: "Dietary Restrictions",
        type: "text",
        required: false,
      },
      {
        id: "questions",
        label: "Questions for Speakers",
        type: "textarea",
        required: false,
      },
    ],
  },
  "2": {
    id: "2",
    title: "Digital Marketing Masterclass",
    description:
      "Learn the latest digital marketing strategies to grow your business online.",
    date: "2023-11-25",
    startTime: "10:00",
    endTime: "15:00",
    location: "Virtual Event",
    address: "Online",
    image:
      "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80",
    ticketTypes: [
      {
        id: "standard",
        name: "Standard Access",
        price: 149,
        description: "Full event access",
      },
      {
        id: "premium",
        name: "Premium Access",
        price: 249,
        description: "Full access plus 1-on-1 consultation",
      },
    ],
    customFields: [
      { id: "company", label: "Company Name", type: "text", required: true },
      { id: "jobTitle", label: "Job Title", type: "text", required: true },
      {
        id: "experience",
        label: "Marketing Experience Level",
        type: "text",
        required: true,
      },
    ],
  },
  "3": {
    id: "3",
    title: "Healthcare Innovation Conference",
    description:
      "Exploring the future of healthcare technology and patient care models.",
    date: "2024-01-10",
    startTime: "08:30",
    endTime: "16:30",
    location: "Boston Medical Center",
    address: "750 Albany St, Boston, MA 02119",
    image:
      "https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80",
    ticketTypes: [
      {
        id: "professional",
        name: "Healthcare Professional",
        price: 249,
        description: "For doctors, nurses, and healthcare staff",
      },
      {
        id: "industry",
        name: "Industry Partner",
        price: 349,
        description: "For healthcare technology companies",
      },
      {
        id: "student",
        name: "Student/Resident",
        price: 99,
        description: "Discounted rate for students and residents",
      },
    ],
    customFields: [
      {
        id: "institution",
        label: "Institution/Hospital",
        type: "text",
        required: true,
      },
      { id: "role", label: "Professional Role", type: "text", required: true },
      {
        id: "specialization",
        label: "Specialization",
        type: "text",
        required: false,
      },
    ],
  },
  "4": {
    id: "4",
    title: "Sustainable Business Forum",
    description:
      "Strategies for implementing sustainable practices in your business operations.",
    date: "2024-02-05",
    startTime: "09:00",
    endTime: "16:00",
    location: "Seattle Convention Center",
    address: "705 Pike St, Seattle, WA 98101",
    image:
      "https://images.unsplash.com/photo-1464692805480-a69dfaafdb0d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80",
    ticketTypes: [
      {
        id: "standard",
        name: "Standard Ticket",
        price: 179,
        description: "Full access to all forum sessions",
      },
      {
        id: "exhibitor",
        name: "Exhibitor Package",
        price: 499,
        description: "Booth space and 2 attendee passes",
      },
    ],
    customFields: [
      { id: "company", label: "Company Name", type: "text", required: true },
      { id: "position", label: "Position", type: "text", required: true },
      {
        id: "interests",
        label: "Sustainability Interests",
        type: "textarea",
        required: false,
      },
    ],
  },
  "5": {
    id: "5",
    title: "AI in Education Summit",
    description:
      "Exploring the applications of artificial intelligence in modern education.",
    date: "2023-12-03",
    startTime: "10:00",
    endTime: "15:00",
    location: "Chicago Educational Center",
    address: "425 S Wabash Ave, Chicago, IL 60605",
    image:
      "https://images.unsplash.com/photo-1517048676732-d65bc937f952?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80",
    ticketTypes: [
      {
        id: "educator",
        name: "Educator Pass",
        price: 129,
        description: "For teachers and educational staff",
      },
      {
        id: "administrator",
        name: "Administrator Pass",
        price: 179,
        description: "For school and district administrators",
      },
      {
        id: "vendor",
        name: "EdTech Vendor",
        price: 249,
        description: "For educational technology companies",
      },
    ],
    customFields: [
      {
        id: "institution",
        label: "School/Institution",
        type: "text",
        required: true,
      },
      { id: "role", label: "Role", type: "text", required: true },
      {
        id: "aiExperience",
        label: "AI Experience Level",
        type: "text",
        required: false,
      },
    ],
  },
  "6": {
    id: "6",
    title: "Global Finance Conference",
    description:
      "Insights into global financial trends and investment strategies for 2024.",
    date: "2024-01-20",
    startTime: "08:00",
    endTime: "18:00",
    location: "New York Financial Center",
    address: "1335 Avenue of the Americas, New York, NY 10019",
    image:
      "https://images.unsplash.com/photo-1551836022-d5d88e9218df?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80",
    ticketTypes: [
      {
        id: "professional",
        name: "Financial Professional",
        price: 349,
        description: "For finance industry professionals",
      },
      {
        id: "executive",
        name: "Executive Pass",
        price: 649,
        description: "For C-suite and senior executives",
      },
      {
        id: "academic",
        name: "Academic/Research",
        price: 199,
        description: "For academics and researchers",
      },
    ],
    customFields: [
      {
        id: "organization",
        label: "Organization",
        type: "text",
        required: true,
      },
      { id: "title", label: "Title/Position", type: "text", required: true },
      {
        id: "interests",
        label: "Financial Interests",
        type: "textarea",
        required: false,
      },
    ],
  },
};

export interface EventData {
  id: string;
  title: string;
  description: string;
  date: string;
  startTime: string;
  endTime: string;
  location: string;
  address: string;
  image: string;
  ticketTypes: Array<{
    id: string;
    name: string;
    price: number;
    description: string;
  }>;
  customFields: Array<{
    id: string;
    label: string;
    type: string;
    required: boolean;
  }>;
}

export interface Ticket {
  ticket_name: string;
  description: string;
  price: string; // string representing decimal e.g. "50.00"
  max_quantity: string; // should be number ideally, but string in your data
  sale_start_date?: string; // optional, ISO date string
  sale_end_date?: string; // optional, ISO date string
}

export interface Event {
  account_type: string;
  category: string;
  created_at: string;
  end_time: string;
  event_date: string; // "YYYY-MM-DD"
  event_description: string;
  event_id: string;
  event_image: string;
  event_title: string;
  img_url: string[]; // Array of image identifiers or URLs
  meeting_link: string;
  pk: string;
  start_time: string; // "HH:MM"
  virtual_event: "yes" | "no";
  location?: string;
  tickets: Ticket[];
  ticket_price?: string;
}

export function useEventData(eventId: string | undefined) {
  const { exploreSingleEvent } = EventsService;
  const { toast } = useToast();
  const navigate = useNavigate();
  const [eventData, setEventData] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // In a real app, this would be an API call
    /** if (eventId && eventsData[eventId as keyof typeof eventsData]) {
      setEventData(eventsData[eventId as keyof typeof eventsData]);
      setLoading(false);
    } else {
      // Handle case where event is not found
      toast({
        title: "Event not found",
        description:
          "The event you're looking for doesn't exist or has been removed.",
        variant: "destructive",
      });
      navigate("/all-events");
    }
    */
    getEventDetailsFn();
  }, [eventId, navigate, toast]);
  const getEventDetailsFn = async () => {
    setLoading(true);
    try {
      const response: any = await exploreSingleEvent(eventId);
      if (response.statusCode === 200) {
        setEventData(response.body.data);
        setLoading(false);
      } else {
        // Handle case where event is not found
        toast({
          title: "Event not found",
          description:
            "The event you're looking for doesn't exist or has been removed.",
          variant: "destructive",
        });
        navigate("/all-events");
      }
    } catch (error) {
      toast({
        title: "Somehing went wrong",
        description: "Somehing went wrong. Kindly try agin.",
        variant: "destructive",
      });
    }
  };
  return { eventData, loading };
}
